export const MISSED_SHIFT_REASON_MANAGER_CANCELLED = "manager_cancelled";

/**
 * The missed-shift reason recorded against a shift, whichever shape it arrives
 * in: a published shift directly, a timecard row's `published`, or a schedule
 * row's `publishedschedule`.
 */
export function getMissedShiftReason(shift) {
  if (!shift) return null;
  return (
    shift.missed_shift_reason ||
    shift.published?.missed_shift_reason ||
    shift.publishedschedule?.missed_shift_reason ||
    null
  );
}

export const MISSED_SHIFT_REASON_LABELS = {
  called_off_sick: "Called out sick",
  called_off_other: "Called out other",
  no_show: "No-call no-show",
  [MISSED_SHIFT_REASON_MANAGER_CANCELLED]: "Manager cancelled",
};

/**
 * How a manager classified a shift nobody worked, or null if they have not.
 * Wording matches the shift inputs modal, so the schedule reads back exactly
 * what was recorded there.
 */
export function getMissedShiftReasonLabel(shift) {
  return MISSED_SHIFT_REASON_LABELS[getMissedShiftReason(shift)] || null;
}

/**
 * A shift the manager called off. It was never meant to be worked, so it
 * carries no scheduled hours and its absence is not an attendance problem.
 */
export function isManagerCancelledShift(shift) {
  return getMissedShiftReason(shift) === MISSED_SHIFT_REASON_MANAGER_CANCELLED;
}

function isNoShowActualShift(shift) {
  return (
    shift?.result_start === "no show" &&
    shift?.result_end === "no show"
  );
}

export function getCoveredPublishedShifts(shift) {
  const covered = Array.isArray(shift?.covered_published_schedules)
    ? shift.covered_published_schedules.filter(Boolean)
    : [];
  if (covered.length > 0) return covered;
  return shift?.published ? [shift.published] : [];
}

export function getCoveredPublishedKey(shift) {
  const covered = getCoveredPublishedShifts(shift);
  if (covered.length === 0) return null;
  return covered
    .map(
      (published) =>
        published.id ?? `${published.shift_start}-${published.shift_end}`
    )
    .join("|");
}

/**
 * What a published shift was worth, the way the reports read it.
 *
 * The hours field is not always present on a published snapshot, so this falls
 * back to the times less unpaid break, and a shift the manager called off was
 * never owed at all. VisualDailyReportModal2 and LiveDayLaborReportModal each
 * carry their own copy of this; new callers should use this one.
 */
export function getPublishedShiftHours(shift) {
  if (!shift || isManagerCancelledShift(shift)) return 0;
  const number = (value) =>
    Number.isFinite(Number(value)) ? Number(value) : 0;

  const unpaid =
    Array.isArray(shift.unpaid_breaks) && shift.unpaid_breaks.length > 0
      ? shift.unpaid_breaks.reduce(
          (sum, entry) =>
            sum + number(entry?.break_length ?? entry?.break_duration ?? 0),
          0
        )
      : number(shift.total_break) ||
        number(shift.break_length) + number(shift.break_length_2);

  if (number(shift.hours) > 0) return number(shift.hours);
  return Math.max(
    0,
    number(shift.shift_end) - number(shift.shift_start) - unpaid
  );
}

export function sumCoveredPublishedHours(shift, getHours) {
  return getCoveredPublishedShifts(shift).reduce(
    (sum, published) => sum + getHours(published),
    0
  );
}

export function filterCoveredNoShowRows(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const coveredIds = new Set();

  safeRows.forEach((row) => {
    if (isNoShowActualShift(row)) return;
    getCoveredPublishedShifts(row).forEach((published) => {
      if (published?.id != null) coveredIds.add(published.id);
    });
  });

  return safeRows.filter((row) => {
    if (!isNoShowActualShift(row)) return true;
    const published = row?.published;
    // A missing punch only implies absence when clock-in was required.
    // Preserve a manager's explicit classification and every real punch.
    if (published?.clock_in_optional === true && !getMissedShiftReason(row)) return false;
    return !(published?.id != null && coveredIds.has(published.id));
  });
}

// Prefer actual coverage even when it explicitly differs from the schedule.
// Null preserves the legacy fallback for unresolved or non-platform entries.
export function isActualNonCoverage(shift) {
  if (typeof shift?.actual_exclude_from_labor === "boolean") {
    return shift.actual_exclude_from_labor;
  }
  return getCoveredPublishedShifts(shift).some(
    (published) => published?.role_id?.exclude_from_labor === true
  );
}
